import { FC, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface Props {
  children: any
  show: boolean
  onHide: () => void
  wide?: boolean
  // When set, closing via X/Esc/backdrop prompts with this message first.
  confirmClose?: string
}

export const Modal: FC<Props> = ({
  children,
  show,
  onHide,
  wide = false,
  confirmClose,
}) => {
  const { t } = useTranslation('common')
  // Keep the latest onHide/confirmClose in refs so the once-registered Escape
  // listener always sees current values without needing to re-bind.
  const onHideRef = useRef(onHide)
  onHideRef.current = onHide
  const confirmCloseRef = useRef(confirmClose)
  confirmCloseRef.current = confirmClose

  // Close the modal, first prompting for confirmation when `confirmClose` is set.
  const requestClose = () => {
    const message = confirmCloseRef.current
    if (message && !window.confirm(message)) return
    onHideRef.current()
  }

  useEffect(() => {
    const escapeKeyClose = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose()
    }
    document.addEventListener('keydown', escapeKeyClose, false)
    return () => document.removeEventListener('keydown', escapeKeyClose, false)
  }, [])

  // Lock background scrolling while the modal is open so it stays centered.
  // Pin the body in place while the modal is open so the page behind can't
  // scroll (overflow:hidden alone doesn't stop window scrolling). The scroll
  // offset is preserved and restored on close/unmount, so the page can never
  // get stuck or jump.
  useEffect(() => {
    if (!show) return

    const scrollY = window.scrollY
    const { body, documentElement } = document
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
    documentElement.style.overflow = 'hidden'

    return () => {
      body.style.position = ''
      body.style.top = ''
      body.style.left = ''
      body.style.right = ''
      body.style.width = ''
      documentElement.style.overflow = ''
      window.scrollTo(0, scrollY)
    }
  }, [show])

  if (!show) {
    return null
  }

  const backdropClose = () => {
    // With a custom confirm message, route through requestClose so the backdrop
    // shows the same single prompt as X/Esc (avoids a double confirmation).
    if (confirmClose) {
      requestClose()
      return
    }
    if (confirm(t('common:modal.confirmClose'))) {
      onHide()
    }
  }

  return createPortal(
    // Full-viewport scroll container: only scrolls when the modal is taller
    // than the screen, otherwise the modal stays centered. Clicking the
    // backdrop (anywhere but the modal box) closes it.
    <div
      className='fixed inset-0 z-100 overflow-y-auto'
      onClick={backdropClose}
    >
      <div
        className='fixed inset-0 bg-gray-500 opacity-75'
        aria-hidden='true'
      ></div>
      <div className='relative flex min-h-full items-center justify-center p-4'>
        <div
          onClick={(event) => event.stopPropagation()}
          className={`relative w-full ${wide ? 'sm:max-w-5xl' : 'sm:max-w-lg'}`}
          role='dialog'
          aria-modal='true'
          aria-labelledby='modal-headline'
        >
          <div className='rounded-lg p-8 bg-white shadow'>
            <div className='absolute right-4 top-4'>
              <button
                className='bg-transparent border border-transparent'
                onClick={requestClose}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  fill='currentColor'
                  className='w-6 h-6 text-gray-700'
                  viewBox='0 0 1792 1792'
                >
                  <path d='M1490 1322q0 40-28 68l-136 136q-28 28-68 28t-68-28l-294-294-294 294q-28 28-68 28t-68-28l-136-136q-28-28-28-68t28-68l294-294-294-294q-28-28-28-68t28-68l136-136q28-28 68-28t68 28l294 294 294-294q28-28 68-28t68 28l136 136q28 28 28 68t-28 68l-294 294 294 294q28 28 28 68z'></path>
                </svg>
              </button>
            </div>
            <div className='bg-white text-left'>
              {typeof children === 'function' && children(requestClose)}
              {typeof children === 'object' && children}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
